import { isMemberOf } from "../validation/narrow";
// SINGLE SOURCE OF TRUTH for product-type conditions (frontend + backend).
//
// Conditions are effectively constant reference data (e.g. pallets =
// grade-a/b/c, ibc-totes = triple-rinsed/reconditioned/…). Edit
// PRODUCT_TYPE_CONDITIONS directly here when a product type's conditions
// change — a rare, engineering-driven event. There is no database copy and
// no sync step: this file IS the source of truth.

export type ConditionOption = {
  label: string;
  value: string;
};

export type ProductTypeSlug =
  | "pallets"
  | "gaylord-boxes"
  | "ibc-totes"
  | "metal-drums"
  | "plastic-drums"
  | "wood-crates"
  | "plastic-pallets"
  | "wooden-spools"
  | "plastic-crates"
  | "bulk-bags"
  | "cardboard-bales"
  | "shipping-boxes"
  | "lumber"
  | "equipment"
  | "moving-boxes";

export const MAIN_PRODUCT_TYPE_SLUGS: readonly ProductTypeSlug[] = [
  "pallets",
  "gaylord-boxes",
  "ibc-totes",
  "metal-drums",
  "plastic-drums",
  "wood-crates",
  "plastic-pallets",
  "wooden-spools",
  "plastic-crates",
  "bulk-bags",
  "cardboard-bales",
  "shipping-boxes",
  "lumber",
  "equipment",
  "moving-boxes",
] as const;

// prettier-ignore
export const PRODUCT_TYPE_CONDITIONS: Record<ProductTypeSlug, readonly ConditionOption[]> = {
  "pallets": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Grade A (Like New)",
      "value": "grade-a"
    },
    {
      "label": "Grade B (Good)",
      "value": "grade-b"
    },
    {
      "label": "Grade C (Fair)",
      "value": "grade-c"
    },
    {
      "label": "Combo (Mixed A/B)",
      "value": "combo"
    },
    {
      "label": "Mixed Load",
      "value": "mixed"
    },
    {
      "label": "Cores (Salvage)",
      "value": "core"
    }
  ],
  "gaylord-boxes": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Like New",
      "value": "like-new"
    },
    {
      "label": "Used (Good)",
      "value": "used"
    },
    {
      "label": "Fair",
      "value": "fair"
    },
    {
      "label": "Damaged",
      "value": "damaged"
    }
  ],
  "ibc-totes": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Rebottled",
      "value": "rebottled"
    },
    {
      "label": "Reconditioned",
      "value": "reconditioned"
    },
    {
      "label": "Rinsed",
      "value": "rinsed"
    },
    {
      "label": "Triple Rinsed",
      "value": "triple-rinsed"
    },
    {
      "label": "Used",
      "value": "used"
    },
    {
      "label": "Dirty",
      "value": "dirty"
    },
    {
      "label": "Damaged",
      "value": "damaged"
    }
  ],
  "metal-drums": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Reconditioned",
      "value": "reconditioned"
    },
    {
      "label": "Used",
      "value": "used"
    },
    {
      "label": "Damaged (Salvage)",
      "value": "damaged"
    }
  ],
  "plastic-drums": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Reconditioned",
      "value": "reconditioned"
    },
    {
      "label": "Used",
      "value": "used"
    },
    {
      "label": "Damaged (Salvage)",
      "value": "damaged"
    }
  ],
  "wood-crates": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Used",
      "value": "used"
    },
    {
      "label": "Export Grade",
      "value": "export-grade"
    }
  ],
  "plastic-pallets": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Like New",
      "value": "like-new"
    },
    {
      "label": "Used",
      "value": "used"
    }
  ],
  "wooden-spools": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Used",
      "value": "used"
    },
    {
      "label": "Damaged",
      "value": "damaged"
    }
  ],
  "plastic-crates": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Like New",
      "value": "like-new"
    },
    {
      "label": "Used",
      "value": "used"
    }
  ],
  "bulk-bags": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Used (1x)",
      "value": "used"
    },
    {
      "label": "Multi-Use",
      "value": "multi-use"
    },
    {
      "label": "Baled/Recycle",
      "value": "bale"
    }
  ],
  "cardboard-bales": [
    {
      "label": "OCC (Old Corrugated Containers)",
      "value": "occ"
    },
    {
      "label": "Mixed Paper",
      "value": "mixed"
    },
    {
      "label": "Sorted Office Paper",
      "value": "sorted"
    }
  ],
  "shipping-boxes": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Like New",
      "value": "like-new"
    },
    {
      "label": "Used",
      "value": "used"
    }
  ],
  "lumber": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Used",
      "value": "used"
    },
    {
      "label": "#1 Premium",
      "value": "1-premium"
    },
    {
      "label": "#2 Prime",
      "value": "2-prime"
    },
    {
      "label": "#2 Standard",
      "value": "2-standard"
    },
    {
      "label": "#3 Utility",
      "value": "3-utility"
    },
    {
      "label": "Select Structural",
      "value": "select-structural"
    },
    {
      "label": "Stud Grade",
      "value": "stud"
    },
    {
      "label": "Reclaimed",
      "value": "reclaimed"
    },
    {
      "label": "A-A (Premium Both Sides)",
      "value": "aa"
    },
    {
      "label": "A-B (Premium/Good)",
      "value": "ab"
    },
    {
      "label": "A-C (Premium/Utility)",
      "value": "ac"
    },
    {
      "label": "B-B (Good Both Sides)",
      "value": "bb"
    },
    {
      "label": "B-C (Good/Utility)",
      "value": "bc"
    },
    {
      "label": "CDX (Sheathing Grade)",
      "value": "cdx"
    },
    {
      "label": "BCX (Better Sheathing)",
      "value": "bcx"
    },
    {
      "label": "ACX (Premium Sheathing)",
      "value": "acx"
    },
    {
      "label": "Industrial/B-Grade",
      "value": "industrial"
    },
    {
      "label": "Shop Grade/Seconds",
      "value": "shop"
    }
  ],
  "equipment": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Like New",
      "value": "like-new"
    },
    {
      "label": "Used",
      "value": "used"
    },
    {
      "label": "Refurbished",
      "value": "refurbished"
    }
  ],
  "moving-boxes": [
    {
      "label": "New",
      "value": "new"
    },
    {
      "label": "Like New (1x Used)",
      "value": "like-new"
    },
    {
      "label": "Used",
      "value": "used"
    }
  ]
}

const LEGACY_CONDITION_MAPPINGS: Partial<
  Record<ProductTypeSlug, Record<string, string>>
> = {
  pallets: {
    "repaired grade a": "grade-a",
    "repaired grade b": "grade-b",
    "repaired grade c": "grade-c",
    cores: "core",
    used: "grade-c",
    broken: "core",
    "grade a": "grade-a",
    "grade b": "grade-b",
    "grade c": "grade-c",
  },
  "plastic-pallets": {
    "grade a": "like-new",
    "grade b": "used",
    recycled: "used",
  },
  "gaylord-boxes": {
    "like new": "like-new",
    good: "used",
    poor: "fair",
  },
  "ibc-totes": {
    "triple rinsed": "triple-rinsed",
  },
  "metal-drums": {
    rinsed: "reconditioned",
  },
  // Plastic-drum imports jammed the "Food Grade" certification into the
  // condition string. Food-grade is its own `specs.foodgrade` boolean, so
  // condition canonicalizes to the wear value only; the migration sets the
  // foodgrade spec separately.
  "plastic-drums": {
    "food grade": "new",
    "new, food grade": "new",
    "used, food grade": "used",
    "rinsed, food grade": "reconditioned",
    rinsed: "reconditioned",
    fair: "used",
  },
  "cardboard-bales": {
    recycled: "occ",
    used: "mixed",
    new: "mixed",
  },
  lumber: {
    construction: "2-standard",
    fair: "used",
  },
  "wood-crates": {
    fair: "used",
  },
  "plastic-crates": {
    fair: "used",
  },
};

export function isValidProductTypeSlug(slug: string): slug is ProductTypeSlug {
  return isMemberOf(MAIN_PRODUCT_TYPE_SLUGS, slug);
}

export function getConditionsForProductType(
  productTypeSlug: string,
): readonly ConditionOption[] {
  if (!isValidProductTypeSlug(productTypeSlug)) {
    return [];
  }
  return PRODUCT_TYPE_CONDITIONS[productTypeSlug] ?? [];
}

export function normalizeConditionValue(value: string): string {
  return value.toLowerCase().trim();
}

export function getLegacyConditionMapping(
  productTypeSlug: string,
  conditionValue: string,
): string | undefined {
  if (!isValidProductTypeSlug(productTypeSlug)) return undefined;
  const mappings = LEGACY_CONDITION_MAPPINGS[productTypeSlug];
  if (!mappings) return undefined;
  const normalized = normalizeConditionValue(conditionValue);
  return mappings[normalized];
}

export function isValidConditionForProductType(
  productTypeSlug: string,
  conditionValue: string,
): boolean {
  const conditions = getConditionsForProductType(productTypeSlug);
  const normalized = normalizeConditionValue(conditionValue);
  return conditions.some(
    (c) => normalizeConditionValue(c.value) === normalized,
  );
}

export function getCanonicalConditionValue(
  productTypeSlug: string,
  conditionValue: string,
): string | undefined {
  const conditions = getConditionsForProductType(productTypeSlug);
  const normalized = normalizeConditionValue(conditionValue);

  const directMatch = conditions.find(
    (c) => normalizeConditionValue(c.value) === normalized,
  );
  if (directMatch) {
    return directMatch.value;
  }

  const legacyMapping = getLegacyConditionMapping(
    productTypeSlug,
    conditionValue,
  );
  if (legacyMapping) {
    return legacyMapping;
  }

  return undefined;
}

export function getConditionLabel(
  productTypeSlug: string,
  conditionValue: string,
): string | undefined {
  const conditions = getConditionsForProductType(productTypeSlug);
  const normalized = normalizeConditionValue(conditionValue);
  const condition = conditions.find(
    (c) => normalizeConditionValue(c.value) === normalized,
  );
  return condition?.label;
}

export function getConditionSortOrder(
  productTypeSlug: string,
  conditionValue: string,
): number {
  const conditions = getConditionsForProductType(productTypeSlug);
  const normalized = normalizeConditionValue(conditionValue);
  const index = conditions.findIndex(
    (c) => normalizeConditionValue(c.value) === normalized,
  );
  return index >= 0 ? index : 99;
}

export function conditionMatches(
  condition1: string | undefined,
  condition2: string | undefined,
): boolean {
  if (!condition1 || !condition2) return false;
  return (
    normalizeConditionValue(condition1) === normalizeConditionValue(condition2)
  );
}
